import { createPortal } from 'react-dom';
import {
  Box,
  Card,
  Typography,
  Chip,
  Grid,
  Paper,
  IconButton,
  Stack,
  ClickAwayListener,
  Tooltip,
  Button,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import VideocamOffOutlinedIcon from '@mui/icons-material/VideocamOffOutlined';
import { useState, useEffect, useRef } from 'react';
import LZString from 'lz-string';
import { fleetApi } from '../../../api/axiosClient';
import { useLanguage } from '../../../utils/i18n';
import { getDeviceStatusInfo } from '../../../utils/vssHelper';
import { timezone } from '../../../utils/timeZone';

import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import MemoryIcon from '@mui/icons-material/Memory';

const statusColor = (rawVehicle, isLoaded = false) => {
  if (!rawVehicle) return 'default';
  const data = rawVehicle.raw || rawVehicle;
  const status = (data.status || rawVehicle.status || '').toString().toLowerCase();
  const engine = (data.engine || rawVehicle.engine || '').toString().toUpperCase();
  const speed = Number(data.speed || rawVehicle.speed) || 0;
  const gps = (data.gps || rawVehicle.gps || '').toString().toLowerCase();

  const isAtPolygon =
    status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
  const isOverSpeedSpatial = data.spatial_id > 0 && data.spatial_max_speed > 0 && speed > data.spatial_max_speed && isLoaded;
  const isUnderSpeedSpatial = data.spatial_id > 0 && data.spatial_min_speed > 0 && speed < data.spatial_min_speed && isLoaded;
  const isStatusOverSpeed = status.includes('over speed') || status.includes('overspeed');
  const isStatusUnderSpeed = status.includes('under speed') || status.includes('underspeed');
  const isGpsUpdated = gps.includes('updated') || gps.includes('update');

  if ((isStatusOverSpeed || isOverSpeedSpatial) && isGpsUpdated) return 'success';
  if ((isStatusUnderSpeed || isUnderSpeedSpatial) && isGpsUpdated) return 'success';

  if (status.includes('delay') || gps.includes('delay')) return 'error';
  if (status.includes('maintenance')) return 'error';

  if ((engine === 'ON' || engine === 'IDLE') && speed > 0 && isGpsUpdated) return 'success';
  if ((engine === 'ON' || engine === 'IDLE') && speed === 0 && isGpsUpdated) return 'warning';

  if (engine === 'OFF') {
    if (isAtPolygon) return 'primary';
    return 'default';
  }

  if (speed > 0 && isGpsUpdated) return 'success';
  if (isAtPolygon) return 'primary';

  return 'default';
};

const getStatusLabel = (rawVehicle, isLoaded = false) => {
  if (!rawVehicle) return '-';
  const data = rawVehicle.raw || rawVehicle;
  const status = (data.status || rawVehicle.status || '').toString().toLowerCase();
  const gps = (data.gps || rawVehicle.gps || '').toString().toLowerCase();
  const engine = (data.engine || rawVehicle.engine || '').toString().toUpperCase();
  const speed = Number(data.speed || rawVehicle.speed) || 0;

  const isAtPolygon =
    status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
  const isOverSpeedSpatial = data.spatial_id > 0 && data.spatial_max_speed > 0 && speed > data.spatial_max_speed && isLoaded;
  const isUnderSpeedSpatial = data.spatial_id > 0 && data.spatial_min_speed > 0 && speed < data.spatial_min_speed && isLoaded;
  const isStatusOverSpeed = status.includes('over speed') || status.includes('overspeed');
  const isStatusUnderSpeed = status.includes('under speed') || status.includes('underspeed');
  const isGpsUpdated = gps.includes('updated') || gps.includes('update');

  if ((isStatusOverSpeed || isOverSpeedSpatial) && isGpsUpdated) return 'Moving';
  if ((isStatusUnderSpeed || isUnderSpeedSpatial) && isGpsUpdated) return 'Moving';

  if (gps.includes('delay')) {
    if (gps.includes('24')) return 'Delay > 24h';
    if (gps.includes('1')) return 'Delay > 1h';
    return 'Delay';
  }
  if (status.includes('delay')) {
    if (status.includes('24')) return 'Delay > 24h';
    if (status.includes('1')) return 'Delay > 1h';
    return 'Delay';
  }
  if (status.includes('maintenance')) return 'Maintenance';

  if ((engine === 'ON' || engine === 'IDLE') && speed > 0 && isGpsUpdated) return 'Moving';
  if ((engine === 'ON' || engine === 'IDLE') && speed === 0 && isGpsUpdated) return 'Idle';

  if (engine === 'OFF') {
    if (isAtPolygon) return 'Parked';
    return 'Stopped';
  }

  if (speed > 0 && isGpsUpdated) return 'Moving';
  if (isAtPolygon) return 'Parked';

  return 'Stopped';
};

const getEngineLabel = (engine) => {
  if (!engine) return '-';
  const e = engine.toUpperCase();
  if (e === 'IDLE') return 'Idle';
  if (e === 'OFFLINE') return 'Offline';
  return engine;
};

const formatDate = (isoString) => {
  if (!isoString || isoString === '-') return '-';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;

    const pad = (num) => String(num).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    const ss = pad(d.getSeconds());

    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  } catch {
    return isoString;
  }
};

export default function PopoverVehicle({ open, anchorEl, selected, onClose, onTripHistory, onOpenVideo, mapInstance }) {
  const { t } = useLanguage();
  const isLoaded = selected?.isLoaded || false;
  const containerRef = useRef(null);
  const [showSharePanel, setShowSharePanel] = useState(false);

  const [shareConfig, setShareConfig] = useState({
    showFleetName: true,
    showBodyName: true,
    showVehicleType: true,
    showStatusVehicle: true,
    showVinNo: true,
    showImei: true,
    showOdometer: true,
    showDriverName: true,
    showLastLocationTime: true,
    showLastConnected: true,
    showLastLocation: true,
    showSpeed: true,
    showSpeedMessage: true,
    showStopDuration: true,
    showEngineStatus: true,
    showTraveled: true,
    showTripTime: true,
    showTotalWorkEngine: true,
    // Kondisi Mesin (Non-EV / Umum)
    showFuel: true,
    showAdBlue: true,
    showEngineTemp: true,
    showVoltage: true,
    // Khusus EV (Muncul Otomatis Jika Terdeteksi)
    showEcuTemp: true,
    showCanBatteryCurrent: true,
    showCanBatteryVolt: true,
    showBatteryPct: true,
    showBatteryHealth: true
  });
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);

  const [durationValue, setDurationValue] = useState(1);
  const [durationUnit, setDurationUnit] = useState('Hours');

  useEffect(() => {
    if (open && selected) {
      setShowSharePanel(false);
      setGeneratedLink('');
    }
  }, [open, selected]);

  if (!open || !selected || !anchorEl || !(anchorEl instanceof HTMLElement)) return null;
  const isCameraNotInstalled = !(selected?.raw?.raw?.integration_imei || selected?.raw?.integration_imei);

  let cameraTooltipNode = t('mapsTracking.liveCamera', 'Live Camera');
  let cameraIconColor = '#485D9A'; // Default blueish

  if (!isCameraNotInstalled && selected?.raw?.vssStatus) {
    const statusInfo = getDeviceStatusInfo(selected.raw.vssStatus.accessMode);
    cameraIconColor = statusInfo.color;

    cameraTooltipNode = (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {statusInfo.tooltip}
        </Typography>
        {selected.raw.vssStatus.lastOnlineTime && (
          <Typography variant="caption" display="block">
            Last Online: {selected.raw.vssStatus.lastOnlineTime}
          </Typography>
        )}
        {selected.raw.vssStatus.lastOfflineTime && (
          <Typography variant="caption" display="block">
            Last Offline: {selected.raw.vssStatus.lastOfflineTime}
          </Typography>
        )}
      </Box>
    );
  }

  const canbus = selected?.raw?.raw?.canbus || {};
  const canBatteryCurrent = canbus.canBatteryCurrent / 10000;
  const canBatteryVolt = canbus.canBatteryVolt / 10;
  const batteryPct = canbus.canBatteryPercentage;
  const batteryHealth = canbus.canBatteryHealth;
  const adBluePct = canbus.canAdBlueLevelPercentage;
  const engineTemp = canbus.canEngineTemp;
  const ecuTemp = canbus.canEcuTemp;

  const hasBatteryData = batteryPct || batteryHealth;
  const hasTempData = engineTemp || ecuTemp;
  const hasAdBlueData = adBluePct != null && adBluePct !== '';

  const canEngineTemp = selected?.raw?.raw?.canbus?.canEngineTemperature || selected?.raw?.raw?.canbus?.canEngineTemp || '-';
  const engineTempereture = Number(canEngineTemp) / 10;
  const voltage = selected?.raw?.batt_volt || '-';

  const bodyName = selected?.raw?.vehicle_name || '-';
  const vinNo = selected?.raw?.info?.chasis_no || '-';
  const displayCanFuel = selected?.raw?.raw?.notif_sensor?.canbus_fuel || '-';

  const handleOpenShare = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setShowSharePanel(true);
    setGeneratedLink('');
    setCopied(false);
  };

  const handleOpenVideo = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (onOpenVideo) {
      onOpenVideo(selected);
      onClose();
    }
  };

  const handleConfigChange = (key) => (event) => {
    setShareConfig({ ...shareConfig, [key]: event.target.checked });
  };

  const handleBackToDetail = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setShowSharePanel(false);
  };
  const handleGenerateLink = async () => {
    const now = Date.now();
    let msToAdd = 0;
    const val = Number(durationValue) || 1;

    if (durationUnit === 'Hours') msToAdd = val * 60 * 60 * 1000;
    if (durationUnit === 'Days') msToAdd = val * 24 * 60 * 60 * 1000;
    if (durationUnit === 'Weeks') msToAdd = val * 7 * 24 * 60 * 60 * 1000;

    const expiresAt = now + msToAdd;

    const companyParent = localStorage.getItem('companyId');
    const companyIdSelected = selected?.raw?.company || selected?.raw?.info?.company;
    const companyNameSelected = selected?.raw?.join?.company_name || selected?.raw?.info?.join?.company_name;

    const rawIdsArray = [companyParent, companyIdSelected].filter(Boolean);
    const uniqueCompanyIds = [...new Set(rawIdsArray)].join(',');

    const cns = {};
    if (companyIdSelected && companyNameSelected) {
      cns[companyIdSelected] = companyNameSelected;
    }

    const payloadToShare = {
      lp: selected.fleet_name || selected.raw?.vehicle_license_plate || selected.license_plate,
      fn: selected.fleet_name || selected.raw?.vehicle_license_plate,
      vt: selected.vehicle_type || selected.raw?.info?.vehicle_type_name,
      im: selected.imei || selected.raw?.info?.imei_no,
      vn: selected.raw?.info?.chasis_no,
      dn: selected.driver_name,
      ak: localStorage.getItem('apikey_jyoti') || localStorage.getItem('apiKey') || '',
      tk: localStorage.getItem('token') || localStorage.getItem('apiKey') || '',
      cid: uniqueCompanyIds || '',
      cns: cns,
      exp: expiresAt,
      cfg: shareConfig
    };
    const baseUrl = window.location.origin;
    const compressed = LZString.compressToEncodedURIComponent(JSON.stringify(payloadToShare));

    try {
      const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || Math.abs(new Date().getTimezoneOffset() / 60);
      const payload = {
        duration: msToAdd,
        encode_base64: compressed
      };
      const res = await fleetApi.post(`/share-link/new?tz=${tz}&translate=en`, payload);
      const sharingCode = res?.data?.sharing_code;
      const unitParam = String(payloadToShare.lp || payloadToShare.fn || '').replace(/[\s-]+/g, '-');

      if (sharingCode) {
        const finalLink = `${baseUrl}/shared/live?data=${sharingCode}&unit=${unitParam}`;
        setGeneratedLink(finalLink);
      } else {
        throw new Error('No sharing_code in response');
      }
    } catch (err) {
      console.error('Failed to generate shared link via API:', err);
      // Fallback
      const unitParam = String(payloadToShare.lp || payloadToShare.fn || '').replace(/[\s-]+/g, '-');
      const finalLink = `${baseUrl}/shared/live?data=${compressed}&unit=${unitParam}`;
      setGeneratedLink(finalLink);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy', err);
    }
  };

  // Removed redundant speedWarning logic; we now rely on selected.pulseReason which is synchronized with notifSettings in Map.jsx.

  const popoverContent = (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        zIndex: 9999,
        pointerEvents: 'auto',
        animation: 'fadeIn 0.2s ease-out forwards',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'scale(0.95)' },
          '100%': { opacity: 1, transform: 'scale(1)' }
        }
      }}
    >
      <ClickAwayListener
        onClickAway={(e) => {
          // If the element is no longer in the DOM (e.g., ShareIcon unmounted), ignore
          if (!document.body.contains(e.target)) return;
          if (e.target.closest('.leaflet-container')) return;
          onClose();
        }}
      >
        <Box sx={{ position: 'relative', pointerEvents: 'auto' }}>
          <Paper
            elevation={10}
            sx={{
              width: 280,
              maxHeight: 380,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              background: '#fff',
              color: '#000',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* HEADER UTAMA */}
            <Box sx={{ p: 1, pb: 1, borderBottom: '1px solid #eee' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {showSharePanel ? t('mapsTracking.shareLocation', 'Share Location') : t('mapsTracking.vehicleDetail', 'Vehicle Detail')}
              </Typography>

              <Box sx={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 0.5 }}>
                {showSharePanel ? (
                  <Tooltip title={t('mapsTracking.backToDetail', 'Back to Detail')}>
                    <IconButton size="small" onClick={handleBackToDetail} sx={{ color: '#485D9A' }}>
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <>
                    {isCameraNotInstalled ? (
                      <Tooltip title="Camera Not Installed">
                        <IconButton size="small" sx={{ color: '#7b7c7e' }}>
                          <VideocamOffOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title={cameraTooltipNode}>
                        <IconButton size="small" onClick={handleOpenVideo} sx={{ color: cameraIconColor }}>
                          <VideocamOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title={t('mapsTracking.shareLiveLocation', 'Share Live Location')}>
                      <IconButton size="small" onClick={handleOpenShare} sx={{ color: '#485D9A' }}>
                        <ShareIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}

                <IconButton size="small" onClick={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {!showSharePanel && (
                <Box
                  sx={{
                    mt: 1,
                    background: '#485D9A',
                    color: '#fff',
                    p: 0.5,
                    borderRadius: 2,
                    textAlign: 'center',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    '&:hover': { background: 'rgb(6, 94, 188)' }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (onTripHistory) {
                      onTripHistory(selected.id, selected);
                    }
                  }}
                >
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                    <LocationOnIcon sx={{ color: '#fff', fontSize: 16 }} />
                    <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.75rem' }}>
                      {t('mapsTracking.tripHistory', 'Trip History')}
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', scrollbarWidth: 'thin', display: 'flex', flexDirection: 'column' }}>
              {showSharePanel ? (
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={1}>
                    {t('mapsTracking.setCustomDuration', 'Set Custom Duration:')}
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    value={durationValue}
                    onChange={(e) => {
                      let val = Number(e.target.value);
                      if (val < 1) val = 1;
                      if (durationUnit === 'Hours' && val > 168) val = 168;
                      if (durationUnit === 'Days' && val > 7) val = 7;
                      if (durationUnit === 'Weeks' && val > 1) val = 1;

                      setDurationValue(val);
                    }}
                    inputProps={{ min: 1 }}
                    sx={{
                      '& .MuiInputBase-root': { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                    }}
                  />

                  <Stack direction="row" spacing={0} sx={{ my: 1 }}>
                    {['Hours', 'Days', 'Weeks'].map((unit) => (
                      <Button
                        key={unit}
                        variant={durationUnit === unit ? 'outlined' : 'outlined'}
                        onClick={() => {
                          setDurationUnit(unit);
                          setDurationValue(1);
                        }}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 0,
                          fontSize: 12,
                          px: 1.5,
                          py: 0.5,
                          borderColor: durationUnit === unit ? '#1976d2' : '#ccc',
                          color: durationUnit === unit ? '#1976d2' : '#333'
                        }}
                      >
                        {unit === 'Weeks' ? 'Week' : unit}
                      </Button>
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.disabled" display="block" mb={2}>
                    {t('mapsTracking.maxOneWeekDur', 'Maximum duration: 1 week (168 hours)')}
                  </Typography>

                  <Typography variant="caption" color="text.secondary" mb={1} display="block" fontWeight="bold">
                    {t('mapsTracking.chooseDataToDisplay', 'Pilih data yang akan tampil:')}
                  </Typography>

                  <Box sx={{ maxHeight: 200, overflowY: 'auto', pr: 1, mb: 2, scrollbarWidth: 'thin' }}>
                    <Typography variant="caption" color="primary" fontWeight="bold" display="block">
                      {t('mapsTracking.vehicleIdentity', 'Identitas Kendaraan')}
                    </Typography>
                    <Stack spacing={0}>
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={shareConfig.showFleetName} onChange={handleConfigChange('showFleetName')} />
                        }
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.fleetName', 'Fleet Name')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={shareConfig.showBodyName} onChange={handleConfigChange('showBodyName')} />}
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.bodyName', 'Body Name')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={shareConfig.showVehicleType} onChange={handleConfigChange('showVehicleType')} />
                        }
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.vehicleType', 'Vehicle Type')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={shareConfig.showStatusVehicle}
                            onChange={handleConfigChange('showStatusVehicle')}
                          />
                        }
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.statusVehicle', 'Status Vehicle')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={shareConfig.showVinNo} onChange={handleConfigChange('showVinNo')} />}
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            VIN No.
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={shareConfig.showImei} onChange={handleConfigChange('showImei')} />}
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            IMEI
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={shareConfig.showDriverName} onChange={handleConfigChange('showDriverName')} />
                        }
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.driverName', 'Driver Name')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </Stack>

                    {/* Grup Operasional */}
                    <Typography variant="caption" color="primary" fontWeight="bold" display="block">
                      {t('mapsTracking.operationalAndTime', 'Operasional & Waktu')}
                    </Typography>
                    <Stack spacing={0}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={shareConfig.showLastLocationTime}
                            onChange={handleConfigChange('showLastLocationTime')}
                          />
                        }
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.lastLocTime', 'Last Loc. Time')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={shareConfig.showLastConnected}
                            onChange={handleConfigChange('showLastConnected')}
                          />
                        }
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.lastConnected', 'Last Connected')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={shareConfig.showLastLocation} onChange={handleConfigChange('showLastLocation')} />
                        }
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.lastLocationAddress', 'Last Location (Address)')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={shareConfig.showSpeed} onChange={handleConfigChange('showSpeed')} />}
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.speed', 'Speed')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={shareConfig.showSpeedMessage} onChange={handleConfigChange('showSpeedMessage')} />
                        }
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.speedMessage', 'Speed Message')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={shareConfig.showOdometer} onChange={handleConfigChange('showOdometer')} />}
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.odometer', 'Odometer')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={shareConfig.showStopDuration} onChange={handleConfigChange('showStopDuration')} />
                        }
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.stopDuration', 'Stop Duration')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={shareConfig.showEngineStatus} onChange={handleConfigChange('showEngineStatus')} />
                        }
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.engineStatus', 'Engine Status')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={shareConfig.showTraveled} onChange={handleConfigChange('showTraveled')} />}
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.distanceTraveled', 'Distance Traveled')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={shareConfig.showTripTime} onChange={handleConfigChange('showTripTime')} />}
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.tripTime', 'Trip Time')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={shareConfig.showTotalWorkEngine}
                            onChange={handleConfigChange('showTotalWorkEngine')}
                          />
                        }
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.totalWorkHours', 'Total Work Hours')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </Stack>

                    {/* Grup BBM / Suhu (Umum) */}
                    <Typography variant="caption" color="primary" fontWeight="bold" display="block">
                      {t('mapsTracking.fuelAndTemp', 'Bahan Bakar & Suhu')}
                    </Typography>
                    <Stack spacing={0}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={shareConfig.showFuel} onChange={handleConfigChange('showFuel')} />}
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.fuelData', 'Fuel Data')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={shareConfig.showAdBlue} onChange={handleConfigChange('showAdBlue')} />}
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            AdBlue
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={shareConfig.showEngineTemp} onChange={handleConfigChange('showEngineTemp')} />
                        }
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.engineTemperature', 'Engine Temperature')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={shareConfig.showVoltage} onChange={handleConfigChange('showVoltage')} />}
                        label={
                          <Typography variant="body2" fontSize="0.75rem">
                            {t('mapsTracking.vehicleVoltage', 'Vehicle Voltage')}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </Stack>

                    {(hasBatteryData || hasTempData) && (
                      <>
                        <Typography variant="caption" color="success.main" fontWeight="bold" display="block">
                          {t('mapsTracking.evOnlyCanbus', 'Khusus EV (Canbus)')}
                        </Typography>
                        <Stack spacing={0} sx={{ pl: 1, borderLeft: '2px solid #4caf50' }}>
                          <FormControlLabel
                            control={
                              <Checkbox size="small" checked={shareConfig.showEcuTemp} onChange={handleConfigChange('showEcuTemp')} />
                            }
                            label={
                              <Typography variant="body2" fontSize="0.75rem">
                                {t('mapsTracking.ecuTemp', 'ECU Temp')}
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={shareConfig.showCanBatteryCurrent}
                                onChange={handleConfigChange('showCanBatteryCurrent')}
                              />
                            }
                            label={
                              <Typography variant="body2" fontSize="0.75rem">
                                {t('mapsTracking.batteryCurrent', 'Battery Current')}
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={shareConfig.showCanBatteryVolt}
                                onChange={handleConfigChange('showCanBatteryVolt')}
                              />
                            }
                            label={
                              <Typography variant="body2" fontSize="0.75rem">
                                {t('mapsTracking.batteryVoltage', 'Battery Voltage')}
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox size="small" checked={shareConfig.showBatteryPct} onChange={handleConfigChange('showBatteryPct')} />
                            }
                            label={
                              <Typography variant="body2" fontSize="0.75rem">
                                {t('mapsTracking.batteryPercentage', 'Battery Percentage')}
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={shareConfig.showBatteryHealth}
                                onChange={handleConfigChange('showBatteryHealth')}
                              />
                            }
                            label={
                              <Typography variant="body2" fontSize="0.75rem">
                                {t('mapsTracking.batteryHealth', 'Battery Health')}
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                        </Stack>
                      </>
                    )}
                  </Box>

                  {generatedLink && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom>
                        {t('mapsTracking.linkReadyToShare', 'Link Siap Dibagikan:')}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={generatedLink}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title={copied ? 'Copied!' : 'Copy Link'} placement="top">
                                <IconButton edge="end" onClick={handleCopyLink} color={copied ? 'success' : 'primary'}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          )
                        }}
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                      />
                    </Box>
                  )}

                  <Box sx={{ mt: 'auto', pt: 1 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleGenerateLink}
                      sx={{ textTransform: 'none', bgcolor: '#485D9A', py: 0.8 }}
                    >
                      {t('mapsTracking.generateLink', 'Generate Link')}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ p: 1 }}>
                  <Card variant="outlined" sx={{ borderRadius: 1, p: 1, mb: 0.5, borderColor: '#485D9A' }}>
                    {selected.pulseReason && (
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          bgcolor: selected.pulseColor === 'error' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                          border: `1px solid ${selected.pulseColor === 'error' ? 'rgba(244, 67, 54, 0.5)' : 'rgba(255, 193, 7, 0.5)'}`,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: selected.pulseColor === 'error' ? '#d32f2f' : '#ed6c02', fontWeight: 800, lineHeight: 1.2 }}
                        >
                          {selected.pulseReason}
                        </Typography>
                      </Box>
                    )}
                    <Typography sx={{ fontSize: 12 }}>
                      Fleet
                      <br />
                      <strong>
                        {selected.fleet_name} / {bodyName} /{selected.vehicle_type}
                      </strong>
                    </Typography>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Status</Typography>
                      <Chip
                        label={getStatusLabel(selected.raw || selected, isLoaded)}
                        size="small"
                        variant="outlined"
                        color={statusColor(selected.raw || selected, isLoaded)}
                      />
                    </Grid>

                    <Typography sx={{ fontSize: 12 }}>
                      VIN No <br /> <strong>{vinNo}</strong>
                    </Typography>
                    {hasTempData && (
                      <Stack direction="row" justifyContent="space-between" sx={{ bgcolor: '#f8fafc', px: 1, borderRadius: 1 }}>
                        {engineTemp && (
                          <Tooltip title="Engine Temp">
                            <Box>
                              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Engine Temp</Typography>
                              <Stack direction="row" alignItems="center" spacing={0.2}>
                                <DeviceThermostatIcon sx={{ fontSize: 14, color: 'error.main' }} />
                                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                  {engineTemp}°C
                                </Typography>
                              </Stack>
                            </Box>
                          </Tooltip>
                        )}
                        {ecuTemp && (
                          <Tooltip title="ECU Temp">
                            <Box>
                              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Ecu Temp</Typography>
                              <Stack direction="row" alignItems="center" spacing={0.2} sx={{ ml: 0.5 }}>
                                <MemoryIcon sx={{ fontSize: 14, color: 'info.main' }} />
                                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                  {ecuTemp}°C
                                </Typography>
                              </Stack>
                            </Box>
                          </Tooltip>
                        )}
                      </Stack>
                    )}
                    <Typography sx={{ fontSize: 12 }}>
                      Odometer <br /> <strong>{(Number(selected.vehicle_odometer_km || 0) / 1000).toFixed(2)} Km</strong>
                    </Typography>
                    <Typography sx={{ fontSize: 12 }}>
                      Driver Name <br />
                      <strong>{selected.driver_name || '-'}</strong>
                    </Typography>
                  </Card>

                  <Card variant="outlined" sx={{ borderRadius: 1, p: 1, borderColor: '#485D9A' }}>
                    <Typography sx={{ fontSize: 12 }}>
                      Last Location Time
                      <br />
                      <strong>{formatDate(selected.last_location_time)}</strong>
                    </Typography>
                    <Typography sx={{ fontSize: 12 }}>
                      Last Connected Time <br />
                      <strong>{formatDate(selected.last_connected_time)}</strong>
                    </Typography>
                    <Typography sx={{ fontSize: 12 }}>
                      Last Location <br />
                      <strong>{selected.last_location?.address}</strong>
                    </Typography>

                    {!selected.last_location?.is_location_updated && (
                      <Chip
                        icon={<LocationOffIcon sx={{ color: 'red !important' }} />}
                        label="Location is not updated"
                        size="small"
                        sx={{ fontSize: 11, background: '#ffebee', color: '#d32f2f' }}
                      />
                    )}
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography fontSize={12}>
                          Speed <br /> <strong> {Number(selected.speed || selected.raw?.speed || 0)} Km/h</strong>
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography fontSize={12}>
                          Speed Message <br />
                          <strong> {selected.speed_message || selected.raw?.speed_message || '-'}</strong>
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography fontSize={12}>
                          Stop Duration <br /> <strong> {selected.stop_duration}</strong>
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography fontSize={12}>
                          Engine Status <br />
                          <strong> {getEngineLabel(selected.engine_status)}</strong>
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography fontSize={12}>
                          Traveled <br /> <strong> {selected.distance_traveled_km} Km</strong>
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography fontSize={12}>
                          Trip Time <br /> <strong>{selected.trip_time}</strong>
                        </Typography>
                      </Grid>
                    </Grid>

                    {!hasBatteryData && (
                      <Grid container justifyContent="space-between" alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography fontSize={12}>
                            Temperature <br /> <strong> {engineTempereture} °C </strong>
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography fontSize={12}>
                            Voltage <br /> <strong> {voltage}</strong>
                          </Typography>
                        </Grid>
                      </Grid>
                    )}

                    {hasBatteryData && (
                      <Grid container justifyContent="space-between" alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography fontSize={12}>
                            Battery Current <br /> <strong>{canBatteryCurrent}</strong>
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography fontSize={12}>
                            Battery Volt <br /> <strong>{canBatteryVolt} A</strong>
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                    <Typography fontSize={12}>
                      Total Work Hours <br /> <strong>{selected.total_work_hours}</strong>
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Box flex={1}>
                        {hasBatteryData ? (
                          <Grid container justifyContent="space-between" alignItems="center">
                            <Grid size={{ xs: 12, md: 6 }}>
                              <Typography fontSize={12}>
                                Battery Percentage <br />
                                <strong> {batteryPct}%</strong>
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <Typography fontSize={12}>
                                Battery Health <br />
                                <strong>{batteryHealth}%</strong>
                              </Typography>
                            </Grid>
                          </Grid>
                        ) : (
                          <Grid container justifyContent="space-between" alignItems="center">
                            <Grid size={{ xs: 12, md: hasAdBlueData ? 6 : 12 }}>
                              <Typography fontSize={12}>
                                Fuel <br />{' '}
                                <strong>
                                  {selected.raw?.raw?.fuel} L <br /> {displayCanFuel}
                                </strong>
                              </Typography>
                            </Grid>
                            {hasAdBlueData && (
                              <Grid size={{ xs: 12, md: 6 }}>
                                <Typography fontSize={12}>
                                  AdBlue <br />
                                  <strong>{adBluePct} %</strong>
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        )}
                      </Box>
                    </Stack>

                    <Box>
                      <Typography fontSize={12}>
                        IMEI <br /> <strong>{selected.imei}</strong>
                      </Typography>
                    </Box>
                  </Card>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </ClickAwayListener>
    </Box>
  );

  return createPortal(popoverContent, anchorEl);
}
